import React, { useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ThemeContext from '../../contexts/ThemeContext';
import i18n from '../../i18n';
import { ChevronRightIcon } from '../PrebookingIcons';

const StepperContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 16px;
  margin-bottom: 32px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const StepWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 16px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const StepList = styled.ol`
  display: block;
  font-weight: 500;
  font-size: ${({ layout }) => layout.mediumFontSize}px;
  line-height: 1.4;
  letter-spacing: 0.5px;
  color: ${({ colors }) => colors.heroDarkBrown};
  list-style-position: outside;
  padding: 0;
  margin: 0;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: ${({ layout }) => layout.smallFontSize}px;
  }
`;

const StepItem = styled.li`
  margin-left: 21px;
  white-space: pre-wrap;
  font-weight: ${({ isActive }) => (isActive ? '700' : '500')};

  @media (max-width: 768px) {
    margin-left: 18px;
  }
`;

const ArrowWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 16px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 0 4px;
  }
`;

const ChevronIconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  @media (max-width: 768px) {
    width: 20px;
    height: 20px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const steps = [
  { number: 1, labelKey: 'translation_order_step_details' },
  { number: 2, labelKey: 'translation_order_step_invoicing' },
  { number: 3, labelKey: 'summary' },
];

const TranslationOrderStepIndicator = ({ currentStep }) => {
  const { colors, layout } = useContext(ThemeContext);

  return (
    <StepperContainer role="navigation" aria-label={i18n('translation_order_steps') || 'Order steps'}>
      {steps.map((step, index) => (
        <React.Fragment key={step.number}>
          <StepWrapper>
            <StepList colors={colors} layout={layout} start={step.number}>
              <StepItem
                isActive={currentStep === step.number}
                aria-current={currentStep === step.number ? 'step' : undefined}
              >
                {i18n(step.labelKey)}
              </StepItem>
            </StepList>
          </StepWrapper>
          {index < steps.length - 1 && (
            <ArrowWrapper aria-hidden="true">
              <ChevronIconWrapper>
                <ChevronRightIcon color={colors.twilight} size={24} />
              </ChevronIconWrapper>
            </ArrowWrapper>
          )}
        </React.Fragment>
      ))}
    </StepperContainer>
  );
};

TranslationOrderStepIndicator.propTypes = {
  currentStep: PropTypes.number.isRequired,
};

export default TranslationOrderStepIndicator;
